import { useEffect } from "react";
import { CheckCircle2, ShieldCheck, Truck, FileText, MapPin, Phone, Wrench, Zap, Cog, Stethoscope, Package, Percent } from "lucide-react";
import Reveal from "@/components/Reveal";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import LabelRequestForm from "@/components/LabelRequestForm";
import { STATES } from "@/constants/states";
import { BIZ } from "@/constants/content";

const TYPES = [
  { icon: Zap, t: "High-speed", d: "Turbines, bearings, chucks and push-button or latch heads — push button and manual overhaul." },
  { icon: Cog, t: "Low-speed", d: "Nosecone overhauls, lowspeed handpiece repair and general maintenance." },
  { icon: Wrench, t: "Electric", d: "Electric highspeed overhauls and complex micro-motor work." },
  { icon: Stethoscope, t: "Surgical", d: "Surgical handpiece repair — call for a custom estimate." },
];

const STEPS = [
  { n: "01", t: "Request a prepaid shipping label", d: "Fill out the label request form below. We review every request by hand — no label is issued automatically." },
  { n: "02", t: "Get your prepaid label by email", d: "Once approved, your USPS prepaid label arrives by email. Print it and attach it to your box." },
  { n: "03", t: "Pack & ship your handpieces", d: "Wrap each handpiece, box it up, remove old labels, and drop it at any USPS location or schedule a free pickup." },
  { n: "04", t: "We repair & ship it back", d: "Once we get the handpieces in, 24–72 hours until they're shipped back to the office — OEM-spec parts, 6-month warranty." },
];

const FAQS = [
  { q: "Do you really serve all 50 states?", a: "Yes — JSL Dental Repair accepts mail-in repair orders from dental offices in all 50 U.S. states and Washington, D.C. Find your state in our coverage list below, request a prepaid shipping label, and ship your handpieces to our repair facility." },
  { q: "How does prepaid shipping work?", a: "We offer prepaid shipping — the label we email is prepaid by JSL with an $18 flat-rate shipping fee added to your repair invoice. No payment up front: once we get the handpieces in, it's 24–72 hours until they're shipped back to your office. New customers take 15% off their first repair order — write a note in your request and we'll apply it." },
  { q: "How long does a mail-in repair take?", a: "Once we get the handpieces in, it's 24–72 hours until they're shipped back to your office. You can follow every step on your secure tracking page." },
  { q: "What repairs are included?", a: "High-speed, low-speed, electric and surgical handpiece repairs — overhauls with new bearings, o-rings, gaskets, turbines and chucks, plus general maintenance. Every overhaul carries a 6-month warranty." },
  { q: "Which brands do you service?", a: "All major brands — Midwest, Star, KaVo, MK-dent, NSK, Bien-Air, Lares, W&H, Athena Champion, Dentex, Vector and more. Parts meet or exceed OEM specifications." },
  { q: "How do I track my handpieces?", a: "Every approved request gets a secure tracking link by email — you'll see when the label is created, when USPS picks it up, and when your handpieces arrive at our facility." },
];

function StatesCoverage() {
  return (
    <div data-testid="states-coverage">
      <p className="mt-2 text-[14px] text-slate-500">
        We accept mail-in repair orders from dental offices in <strong className="text-ink">all 50 U.S. states</strong> and
        Washington, D.C. Search for your state to confirm coverage — yes, yours is on the list.
      </p>
      <div className="mt-6 rounded-[24px] border border-ink/10 bg-white p-6 shadow-[0_10px_40px_rgba(22,50,79,0.08)]">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8">
          {STATES.map(([ab, name]) => (
            <div
              key={ab}
              title={`JSL Dental Repair serves ${name} — mail-in dental handpiece repair`}
              className="flex items-center gap-1.5 rounded-lg border border-ink/10 bg-paper px-2 py-2 text-[11.5px] font-semibold text-ink transition-colors hover:border-brandsky hover:bg-brandsky-soft"
              data-testid={`state-chip-${ab}`}
            >
              <CheckCircle2 className="h-3 w-3 shrink-0 text-brandleaf-deep" />
              {ab}
            </div>
          ))}
        </div>
        <p className="mt-4 flex items-center gap-2 text-[12.5px] text-slate-500">
          <MapPin className="h-4 w-4 text-brandsky-deep" />
          51 of 51 covered — including your practice. Mail-in dental handpiece repair, anywhere in the country.
        </p>
      </div>
    </div>
  );
}

export default function NationwidePage() {
  useEffect(() => {
    document.title = "Nationwide Dental Handpiece Repair — Mail-In Service | JSL Dental Repair";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Nationwide mail-in dental handpiece repair — all 50 states. High-speed, low-speed, electric & surgical handpiece repair with free prepaid shipping labels and a 6-month warranty.");
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "ld-nationwide";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Nationwide dental handpiece repair — mail-in service",
      provider: { "@type": "Dentist", name: "JSL Dental Repair", telephone: "+17162566630", email: "jsldentalrepair@gmail.com", address: { "@type": "PostalAddress", addressLocality: "Buffalo", addressRegion: "NY", addressCountry: "US" } },
      areaServed: { "@type": "Country", name: "United States" },
      hasOfferCatalog: { "@type": "OfferCatalog", name: "Handpiece repair services", itemListElement: [
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "High-speed dental handpiece repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Low-speed dental handpiece repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Electric dental handpiece repair" } },
        { "@type": "Offer", itemOffered: { "@type": "Service", name: "Surgical handpiece repair" } }] },
    });
    document.head.appendChild(ld);
    return () => { document.getElementById("ld-nationwide")?.remove(); };
  }, []);

  return (
    <div className="grain min-h-screen bg-paper">
      <Nav />
      <main>
        <section className="blueprint relative overflow-hidden pb-16 pt-36 lg:pt-44" data-testid="nationwide-page-hero">
          <div className="pointer-events-none absolute -left-32 top-16 h-[420px] w-[420px] rounded-full bg-brandsky/20 blur-[120px]" />
          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <p className="eyebrow flex items-center gap-2 text-brandsky-deep"><MapPin className="h-3.5 w-3.5" /> Now serving all 50 states</p>
            <h1 className="font-display mt-4 max-w-3xl text-[clamp(2.2rem,4.6vw,3.9rem)] font-extrabold leading-[1.02] tracking-[-0.02em] text-ink">
              Nationwide mail-in dental handpiece repair.
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
              Professional high-speed, low-speed, electric, and surgical handpiece repairs for dental offices across
              the country. Request a free prepaid USPS shipping label, mail your handpieces to our repair facility,
              and get them back in 24–72 hours after we receive them.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#label-form" className="btn-lift inline-flex items-center gap-2.5 rounded-full bg-ink px-7 py-4 text-[15px] font-bold text-white shadow-[0_16px_36px_rgba(22,50,79,0.3)] hover:bg-abyss" data-testid="nationwide-hero-label-cta">
                Request a Prepaid Shipping Label
              </a>
              <a href={BIZ.jacob.tel} className="btn-lift inline-flex items-center gap-2.5 rounded-full border border-ink/20 bg-white/80 px-7 py-4 text-[15px] font-bold text-ink backdrop-blur hover:border-ink/50" data-testid="nationwide-hero-call-cta">
                Call or text {BIZ.jacob.phone}
              </a>
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20" data-testid="nationwide-benefits">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Why offices ship to JSL</h2>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: MapPin, t: "All 50 states", d: "Mail-in repairs accepted from every U.S. state and D.C." },
                { icon: Truck, t: "Prepaid shipping label", d: "USPS prepaid labels — one $18 flat-rate shipping fee, added to your repair invoice." },
                { icon: ShieldCheck, t: "6-month warranty", d: "Every overhaul — extendable to 12 months." },
                { icon: Percent, t: "15% off first order", d: "New customers save on their first repair order." },
              ].map((b, i) => (
                <Reveal key={b.t} delay={i * 0.06}>
                  <div className="h-full rounded-2xl border border-ink/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" data-testid="nationwide-benefit-card">
                    <span className="inline-flex rounded-xl bg-brandsky-soft p-2.5"><b.icon className="h-5 w-5 text-brandsky-deep" /></span>
                    <h3 className="font-display mt-3 text-lg font-bold text-ink">{b.t}</h3>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">{b.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20" data-testid="nationwide-coverage">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="eyebrow text-brandsky-deep">Coverage</p>
              <h2 className="font-display mt-2 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">We serve every state. Yes, yours too.</h2>
            </Reveal>
            <Reveal delay={0.08} className="mt-6"><StatesCoverage /></Reveal>
          </div>
        </section>

        <section id="how-it-works" className="py-16 sm:py-20" data-testid="nationwide-process">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="eyebrow text-brandsky-deep">How mail-in repairs work</p>
              <h2 className="font-display mt-2 max-w-2xl text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
                Four steps, one box, zero guesswork.
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div className="h-full rounded-[22px] border border-ink/10 bg-white p-6" data-testid={`nationwide-step-${s.n}`}>
                    <span className="font-mono flex h-10 w-10 items-center justify-center rounded-xl bg-ink text-[14px] font-bold text-white">{s.n}</span>
                    <h3 className="font-display mt-4 text-[16px] font-bold text-ink">{s.t}</h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{s.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20" data-testid="nationwide-types">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="eyebrow text-brandsky-deep">Supported handpiece types</p>
              <h2 className="font-display mt-2 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">What we repair by mail</h2>
            </Reveal>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {TYPES.map((t, i) => (
                <Reveal key={t.t} delay={i * 0.05}>
                  <div className="h-full rounded-2xl border border-ink/10 bg-paper p-6" data-testid={`nationwide-type-${t.t.toLowerCase()}`}>
                    <t.icon className="h-5 w-5 text-brandsky-deep" />
                    <h3 className="font-display mt-3 text-[16px] font-bold text-ink">{t.t}</h3>
                    <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">{t.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 sm:py-20" data-testid="nationwide-shipping-warranty">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="eyebrow text-brandsky-deep">Shipping & warranty</p>
              <h2 className="font-display mt-2 max-w-2xl text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Straightforward from box to bench.</h2>
            </Reveal>
            <div className="mt-8 grid gap-4 lg:grid-cols-3">
              {[
                { icon: FileText, t: "Prepaid labels, approved by a human", d: "Request a label online; our team reviews and approves it, then your prepaid USPS label arrives by email. No label is ever issued automatically." },
                { icon: Truck, t: "24–72 hour turnaround", d: "Once we get the handpieces in, it's 24–72 hours until they're shipped back to your office. Track the package both ways." },
                { icon: ShieldCheck, t: "6-month warranty", d: "Every overhaul carries a 6-month warranty, extendable to 12 months for $49. Parts meet or exceed OEM spec." },
              ].map((c, i) => (
                <Reveal key={c.t} delay={i * 0.06}>
                  <div className="h-full rounded-[22px] border border-ink/10 bg-white p-7">
                    <c.icon className="h-5 w-5 text-brandsky-deep" />
                    <h3 className="font-display mt-3 text-lg font-bold text-ink">{c.t}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-600">{c.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white py-16 sm:py-20" data-testid="nationwide-faq">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="eyebrow text-center text-brandsky-deep">FAQ</p>
              <h2 className="font-display mx-auto mt-2 max-w-xl text-center text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Mail-in repair questions</h2>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="mt-8 space-y-3">
                {FAQS.map((f, i) => (
                  <details key={f.q} className="group rounded-2xl border border-ink/10 bg-paper px-5 py-4" data-testid={`nationwide-faq-${i}`}>
                    <summary className="cursor-pointer list-none text-[15px] font-bold text-ink marker:hidden">
                      {f.q}
                    </summary>
                    <p className="mt-2.5 text-[14px] leading-relaxed text-slate-600">{f.a}</p>
                  </details>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        <section id="label-form" className="scroll-mt-24 bg-brandsky-soft/60 py-16 sm:py-20" data-testid="nationwide-label-form-section">
          <div className="blueprint pointer-events-none absolute inset-0 opacity-40" />
          <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <Reveal>
              <p className="eyebrow text-center text-brandsky-deep">Prepaid shipping label</p>
              <h2 className="font-display mt-2 text-center text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Request a prepaid shipping label</h2>
              <p className="mx-auto mt-3 max-w-xl text-center text-[15px] leading-relaxed text-slate-600">
                Tell us where you are and what you're sending. Our team reviews and approves the request, then your
                prepaid USPS label arrives by email — usually the same business day. Shipping is billed as a single
                $18 flat-rate fee on your repair invoice.
              </p>
            </Reveal>
            <Reveal delay={0.08} className="mt-8">
              <LabelRequestForm />
            </Reveal>
          </div>
        </section>

        <section className="bg-abyss py-16 text-white" data-testid="nationwide-final-cta">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <Package className="mx-auto h-8 w-8 text-brandsky" />
            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight">Ready to ship? We're ready to fix.</h2>
            <p className="mx-auto mt-3 max-w-xl text-[15px] text-white/70">
              Request a free label above, or call us and we'll walk you through it — Jacob (716) 256-6630 or
              Jonah (716) 381-0305.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}